/**
 * The New Connection dialog's start view (Option C, C1 + C3, plan §3.2, prototype `look/start.png`): no engine is chosen
 * yet. A filter (focused on open) over the engine cards, grouped as Databases and Azure services, and the four "Other
 * ways to add". The filter is an editable combobox (`useEngineCombobox`); its row stays at the top of the scroll region
 * (sticky, so it also stays in the very-short tier). The result count is announced politely, 300 ms after typing stops.
 * C1b: the view is a page of the transition (`data-flip-page`); the filter row, hint and no-match line are the parts
 * the rail lacks, so they fade in when the view comes back (`data-flip-key="filter"`, `data-flip-chrome`).
 *
 * C3 — paste detection: filter text that parses as a connection string replaces the list with a detection card (engine,
 * the fields it fills with secrets masked, what is left out and still needed) and Continue; nothing reaches the form
 * before Continue or Enter. Text shaped like a connection string that does not parse shows the failure line instead of
 * the no-match line, so the text itself is never echoed. A string several engines read alike (ruling after C3 review r5)
 * shows the card with "Reads as: …" (`useReadsAs`): Continue and Enter wait until an engine is picked.
 *
 * Final UX review: a pasted string collapses into a chip in the filter row (`StartFilterRow`, UX-03 — the text is never on
 * screen); under a failure line every engine card stays, reachable with the arrows, since the text is a paste and not a
 * filter term (UX-02: "choose the engine" must be doable in place).
 */
import { useEffect, useMemo, useRef, type ReactNode, type RefObject } from 'react';
import type { UserFacingEngineId } from '@/core/engines';
import type { DatabaseEngine } from '../../components/connectionFormSchema';
import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import { redactSecretText } from '@/types/safeConnectionDisplay';
import { parseConnectionString, type ConnectionStringParseResult } from '../../services/connection-string/parseConnectionString';
import type { EntryPointsApi } from '../hooks/useEntryPoints';
import type { FilterVia } from '../hooks/useDialogNavigation';
import { PasteDetectionCard } from '../paste/PasteDetectionCard';
import { PasteParseStatus } from '../paste/PasteParseStatus';
import { choiceHeadline, looksLikeConnectionString, pasteChoiceMessage, pasteFailedMessage, pastedChipLabel, previewSentence, type PastePreview } from '../paste/pastePreview';
import { usePasteTracking } from '../paste/usePasteTracking';
import { useReadsAs } from '../paste/useReadsAs';
import { FUNNEL_VERSION } from '../hooks/useConnectionDialogFunnel';
import { EngineOptionList } from './EngineOptionList';
import { StartFilterRow } from './StartFilterRow';
import { filterAnnouncement, filterEngines, noMatchMessage, parseStartOption, type StartOptionId } from './engineFilter';
import { useEngineCombobox } from './useEngineCombobox';

/** The placeholder already says "filter or paste" (UX-11): the hint names the keys, and what Esc does here. */
export const START_HINT = 'Arrow keys move, Enter chooses, Esc clears the filter or closes.';
/** The hint under a detection card, and under one that asks which engine (UX review r2, R-5). */
export const CARD_HINT = 'Enter continues, Esc clears the connection string.';
export const CHOICE_HINT = 'Pick the engine it is for, then press Enter. Esc clears the connection string.';
/** Takes the hint line's place (C3 review C3-09: a separate line pushed Detect and Import below the fold). */
export const PASTE_HINT = 'No connection string on the clipboard. Paste one into the filter.';
const ANNOUNCE_DEBOUNCE_MS = 300;

type Props = {
  filter: string;
  filterVia: FilterVia;
  onFilterChange: (text: string, via: FilterVia) => void;
  /** Back from the form: the engine the user left is the active option. */
  initialActive: StartOptionId | null;
  onPickEngine: (engine: UserFacingEngineId, via: 'card' | 'filter_enter') => void;
  entries: EntryPointsApi;
  paste: {
    previewFor: (result: ConnectionStringParseResult) => PastePreview | null;
    onContinue: (result: ConnectionStringParseResult) => void;
    /** The Paste entry found nothing on the clipboard. */
    hint: boolean;
  };
  /** What a nested flow (import, Detect) saved while the dialog stayed open (C3). */
  notice: ReactNode;
  announce: (message: string) => void;
  filterRef: RefObject<HTMLInputElement>;
  short: boolean;
  track: TrackFunction;
};

export function EngineStartView({ filter, filterVia, onFilterChange, initialActive, onPickEngine, entries, paste, notice, announce, filterRef, short, track }: Props) {
  const parsed = useMemo(() => (filter.trim() ? parseConnectionString(filter) : null), [filter]);
  // Several engines read it alike: the picked engine's reading is the one shown and applied.
  const readsAs = useReadsAs(filter, parsed, track);
  const shown = readsAs.result;
  const preview = shown ? paste.previewFor(shown) : null;
  const choosing = readsAs.choices !== undefined;
  const failedPaste = !!parsed && !preview && !choosing && (parsed.refusal !== undefined || looksLikeConnectionString(filter));
  // A failed paste is not a filter term: every engine stays listed under the failure line (UX-02).
  const listFilter = failedPaste ? '' : filter;
  const { databases, azure, any } = filterEngines(listFilter);
  // A pasted (or handed-over) string the start view read collapses into a chip (UX-03); a string typed by hand stays text.
  const chip = (filterVia === 'paste' || filterVia === 'prefill') && (preview || choosing || failedPaste) ? pastedChipLabel(preview, parsed?.format) : null;
  const clearChip = (via: 'button' | 'key') => {
    onFilterChange('', 'type');
    track('ui:connection_string_cleared', { source: 'start_filter', via, funnel_version: FUNNEL_VERSION });
  };
  const tracking = usePasteTracking('start_filter', track);
  const continuePaste =
    preview && shown
      ? () => {
          // The parse event of what was just typed goes out before the apply (C3 review C3-07).
          tracking.flush();
          paste.onContinue(shown);
        }
      : undefined;
  const pick = (engine: DatabaseEngine) => {
    readsAs.choose(engine);
    const picked = paste.previewFor(parseConnectionString(filter, { engine }));
    if (picked) announce(`${previewSentence(picked)} Press Enter to continue with ${picked.engineLabel}.`);
  };

  const activate = (id: StartOptionId, via: 'enter' | 'click') => {
    const option = parseStartOption(id);
    if (option.kind === 'engine') onPickEngine(option.engine, via === 'enter' ? 'filter_enter' : 'card');
    else entries.activate(option.entry, 'start');
  };
  // While the card asks which engine, Enter waits for the pick (the card replaced the list: there is no option).
  const combobox = useEngineCombobox({ filter: listFilter, initialActive, onActivate: activate, onContinue: continuePaste ?? (choosing ? () => undefined : undefined) });

  useEffect(() => {
    // A palette handoff was reported by the palette (source command_palette): not again as a filter paste.
    tracking.observe(filter, filterVia !== 'prefill' && (preview || failedPaste || choosing) ? parsed : null, filterVia === 'paste');
    // The parse shown for this text; `filterVia` travels with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const announced = useRef(filter);
  useEffect(() => {
    if (announced.current === filter) return;
    const message = preview
      ? `${previewSentence(preview)} Press Enter to continue with ${preview.engineLabel}.`
      : choosing && parsed
        ? `${choiceHeadline(parsed.format)}. ${pasteChoiceMessage(readsAs.choices ?? [])}`
        : failedPaste ? pasteFailedMessage(parsed) : redactSecretText(filterAnnouncement(filter));
    const timer = window.setTimeout(() => {
      announced.current = filter;
      announce(message);
    }, ANNOUNCE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // The message follows the filter it was computed for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, announce]);

  const trimmed = filter.trim();
  return (
    <div data-testid="connection-start-view" data-flip-page data-flip-scroller className="min-h-0 flex-1 overflow-y-auto px-6 pb-5">
      <StartFilterRow
        filter={filter}
        onFilterChange={onFilterChange}
        chip={chip}
        onClearChip={clearChip}
        onKeyDown={combobox.onKeyDown}
        activeDescendant={combobox.activeDescendant}
        expanded={!preview && !choosing}
        keys={continuePaste ? 'continue' : choosing ? 'none' : 'navigate'}
        filterRef={filterRef}
        short={short}
      />
      {paste.hint && !trimmed ? (
        <p id="engine-filter-hint" data-flip-chrome="fade" className="mb-3 text-xs text-foreground" role="status" data-testid="connection-string-paste-hint">
          {PASTE_HINT}
        </p>
      ) : failedPaste ? (
        // The failure line is the hint (UX review r2, R-4): one line fewer keeps "Other ways to add" in view.
        <div id="engine-filter-hint" className="mb-3" data-flip-chrome="fade">
          <PasteParseStatus preview={null} failed refusal={parsed ?? undefined} />
        </div>
      ) : (
        <p id="engine-filter-hint" data-flip-chrome="fade" className="mb-3 text-xs text-muted-foreground">
          {continuePaste ? CARD_HINT : choosing ? CHOICE_HINT : START_HINT}
        </p>
      )}
      {notice}
      {(preview && continuePaste) || choosing ? (
        <PasteDetectionCard
          preview={preview}
          format={parsed?.format ?? 'unknown'}
          choice={readsAs.choices ? { engines: readsAs.choices, picked: readsAs.picked, onPick: pick } : undefined}
          onContinue={continuePaste}
        />
      ) : (
        <>
          {!any && !failedPaste && (
            <p className="mb-3 break-words text-sm text-muted-foreground" data-flip-chrome="fade" data-testid="engine-filter-no-match">
              {noMatchMessage(redactSecretText(trimmed))}
            </p>
          )}
          <EngineOptionList
            databases={databases}
            azure={azure}
            active={combobox.active}
            onActivate={(id) => {
              combobox.setActive(id);
              activate(id, 'click');
            }}
            entries={entries}
            onMenuClosed={() => filterRef.current?.focus()}
          />
        </>
      )}
    </div>
  );
}
